import { cartKeys, useUserCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
  isDeviceCodeRejected,
  updateLocalCartUuid,
  useGetCartForUser,
  useKioskDeviceGrant,
  useKioskDeviceSession,
  useLocalCartUuid,
  v1CartKeys,
} from '@jane/kiosk/data-access';
import type { DeviceGrant } from '@jane/kiosk/data-access/lib/authentication/authentication.types';
import { getUsername } from '@jane/kiosk/util';
import { getJwt } from '@jane/shared/auth';
import type { Store } from '@jane/shared/types';

const DEFAULT_POLL_SECONDS = 5;

/** The server rejects polls that arrive inside its own interval, so stay just outside it. */
const POLL_MARGIN_MS = 500;

export const useKioskUserSession = ({ store }: { store: Store }) => {
  const queryClient = useQueryClient();
  const cartUuid = useLocalCartUuid();
  const [signedIn, setSignedIn] = useState(false);
  const [deviceGrant, setDeviceGrant] = useState<DeviceGrant | null>(null);
  const [qrCodeError, setQRCodeError] = useState<string | null>(null);

  const { mutate: createDeviceGrant, isPending } = useKioskDeviceGrant({
    onSuccess: (grant: DeviceGrant) => {
      setDeviceGrant(grant);
      setQRCodeError(null);
    },
    onError: () => {
      setQRCodeError(
        'We could not generate your QR code, please go back and try again. If this problem persists, please check out as a guest and let a budtender know.'
      );
    },
  });

  useEffect(() => {
    createDeviceGrant();
  }, []);

  const { data: kioskUser, error } = useKioskDeviceSession(
    {
      deviceCode: deviceGrant?.device_code,
    },
    {
      enabled: !signedIn && !!deviceGrant,
      refetchInterval:
        (deviceGrant?.interval ?? DEFAULT_POLL_SECONDS) * 1000 + POLL_MARGIN_MS,
    }
  );

  useEffect(() => {
    if (kioskUser) setSignedIn(true);
  }, [kioskUser]);

  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);

  const { data: userCart } = useUserCart(
    {
      appMode: 'kiosk',
      storeId: store?.id,
      uuid: cartUuid ?? undefined,
      jwt: getJwt(),
    },
    { enabled: !!useV3Cart && !!kioskUser && !!cartUuid }
  );

  useEffect(() => {
    if (!userCart) return;
    updateLocalCartUuid(userCart.uuid);
    queryClient.setQueryData(cartKeys.detail(userCart.uuid), userCart);
  }, [userCart, queryClient]);

  const { data: legacyCart } = useGetCartForUser(
    {
      app_mode: 'kiosk',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      uuid: cartUuid!,
      store_id: store?.id,
    },
    { enabled: !useV3Cart && !!kioskUser && !!cartUuid }
  );

  useEffect(() => {
    if (!legacyCart) return;
    queryClient.setQueryData(v1CartKeys.detail(cartUuid), legacyCart);
    queryClient.invalidateQueries({ queryKey: v1CartKeys.detail(cartUuid) });
  }, [legacyCart, cartUuid, queryClient]);

  const userName = useMemo(() => getUsername(kioskUser), [kioskUser]);

  /**
   * The grant is single use and short lived. Once the server rejects,
   * it must be replaced on the screen.
   */
  useEffect(() => {
    if (signedIn || isPending || !deviceGrant || !isDeviceCodeRejected(error)) {
      return;
    }

    createDeviceGrant();
  }, [error, signedIn, isPending, deviceGrant]);

  return {
    deviceGrant,
    userName,
    signedIn,
    isLoading: isPending && !deviceGrant,
    isReplacingQRCode: isPending && !!deviceGrant,
    qrCodeError,
  };
};
